import { Buffer } from "buffer";
import { StrictMode, useState, useEffect } from "react";
import { createRoot } from "react-dom/client";

// Make Buffer available globally for @solana/web3.js and other Node.js libs
// biome-ignore lint/suspicious/noExplicitAny: global Buffer polyfill
if (!(globalThis as any).Buffer) {
	// biome-ignore lint/suspicious/noExplicitAny: global Buffer polyfill
	(globalThis as any).Buffer = Buffer;
}
import { DynamicContextProvider } from "@dynamic-labs/sdk-react-core";
import { SolanaWalletConnectors } from "@dynamic-labs/solana";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { App } from "./App";
import { registerServiceWorker } from "./registerServiceWorker";
import "./styles/index.css";

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: true,
			retry: 2,
		},
	},
});

const DYNAMIC_ENV_ID = import.meta.env.VITE_DYNAMIC_ENV_ID as string | undefined;

function getInitialTheme(): "light" | "dark" {
	const requestedTheme = new URLSearchParams(window.location.search).get("theme");
	if (requestedTheme === "light" || requestedTheme === "dark") return requestedTheme;

	return (localStorage.getItem("oblq-theme") as "light" | "dark") || "dark";
}

const INITIAL_THEME = getInitialTheme();
document.documentElement.setAttribute("data-theme", INITIAL_THEME);
registerServiceWorker();

if (!DYNAMIC_ENV_ID) {
	document.getElementById("root")!.innerHTML = `
		<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;font-family:Sora,sans-serif;background:#000;color:#f7fff4;padding:24px">
			<div style="max-width:420px;text-align:center;display:flex;flex-direction:column;gap:16px">
				<h2 style="margin:0;font-size:18px;font-weight:700">Missing Dynamic Environment ID</h2>
				<p style="margin:0;font-size:14px;color:#b6c8af;line-height:1.6">
					Copy <code style="background:#102907;color:#24ff00;padding:2px 6px;border-radius:4px">.env.example</code> to
					<code style="background:#102907;color:#24ff00;padding:2px 6px;border-radius:4px">.env</code>
					and set <code style="background:#102907;color:#24ff00;padding:2px 6px;border-radius:4px">VITE_DYNAMIC_ENV_ID</code>
					to your environment ID from the Dynamic dashboard, then restart the dev server.
				</p>
			</div>
		</div>
	`;
	throw new Error("VITE_DYNAMIC_ENV_ID is not set — see .env.example");
}

function Root() {
	const [theme, setTheme] = useState<"light" | "dark">(INITIAL_THEME);

	useEffect(() => {
		document.documentElement.setAttribute("data-theme", theme);
		localStorage.setItem("oblq-theme", theme);
	}, [theme]);

	return (
		<DynamicContextProvider
			theme={theme}
			settings={{
				environmentId: DYNAMIC_ENV_ID!,
				walletConnectors: [SolanaWalletConnectors],
				appName: "OBLIQ",
			}}
		>
			<App theme={theme} onThemeChange={setTheme} />
		</DynamicContextProvider>
	);
}

const root = document.getElementById("root");
if (!root) throw new Error("Root element not found");

createRoot(root).render(
	<StrictMode>
		<QueryClientProvider client={queryClient}>
			<Root />
		</QueryClientProvider>
	</StrictMode>,
);
